import { forkJoin, of } from 'rxjs';
// Angular
import { Injectable } from '@angular/core';
// RxJS
import { map, mergeMap, catchError } from 'rxjs/operators';
// NGRX
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
// CRUD
import { QueryParamsModel } from '../../_base/crud';
// Services
import { PatientHeightsService } from '../_services/patient-heights.service';
// State
import { AppState } from '../../reducers';
// Actions
import {
  PatientHeightActionToggleLoading,
  PatientHeightActionTypes,
  PatientHeightCreated,
  PatientHeightOnServerCreated,
  PatientHeightsPageLoaded,
  PatientHeightsPageRequested,
  PatientHeightsPageToggleLoading,
  PatientHeightStatusUpdated,
  PatientHeightsStatusUpdated,
  PatientHeightUpdated,
  ManyPatientHeightsDeleted,
  OnePatientHeightDeleted,
  ThrowPatientHeightError,
  ClearPatientHeightErrors,
  PatientHeightsStatusOnServerUpdated,
  PatientHeightStatusOnServerUpdated,
  PatientHeightOnServerUpdated,
  ManyPatientHeightsOnServerDeleted,
  OnePatientHeightOnServerDeleted,
  PatientHeightsPageCancelled,
} from '../_actions/patient-height.actions';
import { Router } from '@angular/router';

@Injectable()
export class PatientHeightEffects {
  showPageLoadingDistpatcher = new PatientHeightsPageToggleLoading({ isLoading: true });
  showActionLoadingDistpatcher = new PatientHeightActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new PatientHeightActionToggleLoading({ isLoading: false });

  @Effect()
  loadPatientHeightsPage$ = this.actions$.pipe(
    ofType<PatientHeightsPageRequested>(PatientHeightActionTypes.PatientHeightsPageRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.patientsService.findPatientHeights(payload.page, payload.id);
      const lastQuery = of(payload.page);
      return forkJoin(requestToServer, lastQuery).pipe(
        catchError((error) => {
          return of([error]);
        }),
        map((response) => {
          if (response[0].error) {
            if (response[0].status === 401) {
              return response[0];
            } else if (response[0].status === 403) {
              this.router.navigateByUrl('/error/403');
              return new PatientHeightsPageCancelled();
            } else {
              this.store.dispatch(new ThrowPatientHeightError({ error: response[0].error }));
              return new PatientHeightsPageCancelled();
            }
          } else {
            const result = response[0];
            // tslint:disable-next-line:no-shadowed-variable
            const lastQuery: QueryParamsModel = response[1];
            this.store.dispatch(new ClearPatientHeightErrors());
            const sortedResult = result.data.sort((a, b) => {
              const x = a[`start_date`];
              const y = b[`start_date`];
              return x < y ? -1 : x > y ? 1 : 0;
            });
            const mappedResult = [...Array(sortedResult.length).keys()].map((i) =>
              Object.assign(result.data[i], { id: i + 1 }),
            );
            return new PatientHeightsPageLoaded({
              patientHeights: mappedResult,
              totalCount: result.count,
              page: lastQuery,
            });
          }
        }),
      );
    }),
  );

  @Effect()
  deletePatientHeight$ = this.actions$.pipe(
    ofType<OnePatientHeightOnServerDeleted>(
      PatientHeightActionTypes.OnePatientHeightOnServerDeleted,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService.deletePatientHeight(payload.id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPatientHeightError({ error: res.error });
          } else {
            this.store.dispatch(new ClearPatientHeightErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new OnePatientHeightDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  deletePatientHeights$ = this.actions$.pipe(
    ofType<ManyPatientHeightsOnServerDeleted>(
      PatientHeightActionTypes.ManyPatientHeightsOnServerDeleted,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService.deletePatientHeights(payload.ids).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowPatientHeightError({ error: res.error });
          } else {
            this.store.dispatch(new ClearPatientHeightErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new ManyPatientHeightsDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updatePatientHeight$ = this.actions$.pipe(
    ofType<PatientHeightOnServerUpdated>(PatientHeightActionTypes.PatientHeightOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService.updatePatientHeight(payload.patientHeight).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPatientHeightError({ error: res.error });
          } else {
            this.store.dispatch(new ClearPatientHeightErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new PatientHeightUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updatePatientHeightStatus$ = this.actions$.pipe(
    ofType<PatientHeightStatusOnServerUpdated>(
      PatientHeightActionTypes.PatientHeightStatusOnServerUpdated,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService.updateStatusForPatientHeight(payload.id, payload.is_active).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowPatientHeightError({ error: res.error });
          } else {
            this.store.dispatch(new ClearPatientHeightErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new PatientHeightStatusUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updatePatientHeightsStatus$ = this.actions$.pipe(
    ofType<PatientHeightsStatusOnServerUpdated>(
      PatientHeightActionTypes.PatientHeightsStatusOnServerUpdated,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService
        .updateStatusForPatientHeights(payload.ids, payload.is_active)
        .pipe(
          catchError((error) => {
            return of({ error });
          }),
          map((res) => {
            if (res.error) {
              return new ThrowPatientHeightError({ error: res.error });
            } else {
              this.store.dispatch(new ClearPatientHeightErrors());
              this.store.dispatch(this.hideActionLoadingDistpatcher);
              return new PatientHeightsStatusUpdated(payload);
            }
          }),
        );
    }),
  );

  @Effect()
  createPatientHeight$ = this.actions$.pipe(
    ofType<PatientHeightOnServerCreated>(PatientHeightActionTypes.PatientHeightOnServerCreated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsService.createPatientHeight(payload.patientHeight, payload.id).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPatientHeightError({ error: res });
          } else {
            this.store.dispatch(new ClearPatientHeightErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new PatientHeightCreated({ patientHeight: res.data });
          }
        }),
      );
    }),
  );

  constructor(
    private actions$: Actions,
    private patientsService: PatientHeightsService,
    private store: Store<AppState>,
    private router: Router,
  ) {}
}
